import { formatDateInTimezone, formatTimeRangeInTimezone } from "@notemeal/utils-date-time";
import { enUS } from "date-fns/locale";
import { AdvancedSelectionCondition, AdvancedSelectionState } from "../../Tags/reducers/advancedSelectionReducers";

export interface SharingRecipientSummary {
  teams: string[];
  positions: string[];
  athletes: string[];
  tags: string[];
  excludedTeams: string[];
  excludedPositions: string[];
  excludedAthletes: string[];
  excludedTags: string[];
}

export interface SharingAthleteRef {
  firstName: string;
  lastName: string;
}

export type SharingCondition =
  | {
      __typename: "TeamTag";
      isComplement: boolean;
      teams: readonly { name: string }[];
    }
  | {
      __typename: "PositionTag";
      isComplement: boolean;
      positions: readonly { name: string }[];
    }
  | {
      __typename: "ComboTag";
      isComplement: boolean;
      namedChildTags: readonly { tagName: { name: string } }[];
    };

export interface SharingAdvancedSelection {
  includeAthletes: readonly SharingAthleteRef[];
  excludeAthletes: readonly SharingAthleteRef[];
  groups: readonly {
    conditions: readonly SharingCondition[];
  }[];
}

export interface SharingIdentityAthlete {
  id: string;
}

export type SharingIdentityCondition =
  | {
      __typename: "TeamTag";
      isComplement: boolean;
      teams: readonly { id: string }[];
    }
  | {
      __typename: "PositionTag";
      isComplement: boolean;
      positions: readonly { id: string }[];
    }
  | {
      __typename: "ComboTag";
      isComplement: boolean;
      combinationMethod: string;
      namedChildTags: readonly { tag: { id: string } }[];
    };

export interface SharingIdentitySelection {
  includeAthletes: readonly SharingIdentityAthlete[];
  excludeAthletes: readonly SharingIdentityAthlete[];
  groups: readonly {
    conditions: readonly SharingIdentityCondition[];
  }[];
}

export type CurrentSharingStatus = "ready" | "loading" | "error";

export interface ComparableMenuSharing {
  status: CurrentSharingStatus;
  identityKey: string;
}

const emptySummary = (): SharingRecipientSummary => ({
  teams: [],
  positions: [],
  athletes: [],
  tags: [],
  excludedTeams: [],
  excludedPositions: [],
  excludedAthletes: [],
  excludedTags: [],
});

const uniqueSorted = (names: readonly string[]): string[] =>
  [...new Set(names.map(name => name.trim()).filter(name => name.length > 0))].sort((a, b) => a.localeCompare(b));

const finalize = (summary: SharingRecipientSummary): SharingRecipientSummary => ({
  teams: uniqueSorted(summary.teams),
  positions: uniqueSorted(summary.positions),
  athletes: uniqueSorted(summary.athletes),
  tags: uniqueSorted(summary.tags),
  excludedTeams: uniqueSorted(summary.excludedTeams),
  excludedPositions: uniqueSorted(summary.excludedPositions),
  excludedAthletes: uniqueSorted(summary.excludedAthletes),
  excludedTags: uniqueSorted(summary.excludedTags),
});

const athleteName = (athlete: SharingAthleteRef) => `${athlete.firstName} ${athlete.lastName}`.trim();

const pushNames = (included: string[], excluded: string[], isComplement: boolean, names: readonly string[]) => {
  (isComplement ? excluded : included).push(...names);
};

export const summarizeTeamNames = (teams: readonly { name: string }[] | null): SharingRecipientSummary =>
  finalize({
    ...emptySummary(),
    teams: teams?.map(team => team.name) ?? [],
  });

export const summarizeAdvancedSelection = (selection: SharingAdvancedSelection): SharingRecipientSummary => {
  const summary = emptySummary();
  summary.athletes.push(...selection.includeAthletes.map(athleteName));
  summary.excludedAthletes.push(...selection.excludeAthletes.map(athleteName));

  selection.groups.forEach(group => {
    group.conditions.forEach(condition => {
      if (condition.__typename === "TeamTag") {
        pushNames(
          summary.teams,
          summary.excludedTeams,
          condition.isComplement,
          condition.teams.map(team => team.name)
        );
      } else if (condition.__typename === "PositionTag") {
        pushNames(
          summary.positions,
          summary.excludedPositions,
          condition.isComplement,
          condition.positions.map(position => position.name)
        );
      } else if (condition.__typename === "ComboTag") {
        pushNames(
          summary.tags,
          summary.excludedTags,
          condition.isComplement,
          condition.namedChildTags.map(tag => tag.tagName.name)
        );
      }
    });
  });

  return finalize(summary);
};

export const summarizeAdvancedSelectionState = (state: AdvancedSelectionState): SharingRecipientSummary => {
  const summary = emptySummary();
  summary.athletes.push(...state.includeAthletes.map(athleteName));
  summary.excludedAthletes.push(...state.excludeAthletes.map(athleteName));

  state.groups.forEach(group => {
    group.conditions.forEach(condition => {
      if (condition.__typename === "Teams") {
        pushNames(
          summary.teams,
          summary.excludedTeams,
          condition.isComplement === true,
          condition.teams.map(team => team.name)
        );
      } else if (condition.__typename === "Positions") {
        pushNames(
          summary.positions,
          summary.excludedPositions,
          condition.isComplement === true,
          condition.positions.map(position => position.name)
        );
      } else if (condition.__typename === "Tags") {
        pushNames(
          summary.tags,
          summary.excludedTags,
          condition.isComplement === true,
          condition.tags.map(tag => tag.tagName.name)
        );
      }
    });
  });

  return finalize(summary);
};

export const isSharingSummaryEmpty = (summary: SharingRecipientSummary): boolean =>
  summary.teams.length === 0 &&
  summary.positions.length === 0 &&
  summary.athletes.length === 0 &&
  summary.tags.length === 0 &&
  summary.excludedTeams.length === 0 &&
  summary.excludedPositions.length === 0 &&
  summary.excludedAthletes.length === 0 &&
  summary.excludedTags.length === 0;

export const formatNewSharingLabel = (summary: SharingRecipientSummary): string => {
  if (isSharingSummaryEmpty(summary)) {
    return "No recipients selected.";
  }

  const included = [...summary.teams, ...summary.positions, ...summary.athletes, ...summary.tags];
  const excluded = [...summary.excludedTeams, ...summary.excludedPositions, ...summary.excludedAthletes, ...summary.excludedTags];
  const parts = [];
  if (included.length > 0) {
    parts.push(included.join(", "));
  }
  if (excluded.length > 0) {
    parts.push(`Excludes ${excluded.join(", ")}`);
  }
  return parts.join(" · ");
};

const sortedIds = (ids: readonly string[]) => [...ids].sort();

const conditionIdentityKey = (condition: SharingIdentityCondition): string => {
  if (condition.__typename === "TeamTag") {
    return `teams:${condition.isComplement}:${sortedIds(condition.teams.map(team => team.id)).join(",")}`;
  }
  if (condition.__typename === "PositionTag") {
    return `positions:${condition.isComplement}:${sortedIds(condition.positions.map(position => position.id)).join(",")}`;
  }
  return `tags:${condition.isComplement}:${condition.combinationMethod}:${sortedIds(condition.namedChildTags.map(tag => tag.tag.id)).join(
    ","
  )}`;
};

const combinationMethodOf = (condition: AdvancedSelectionCondition): string =>
  "combinationMethod" in condition && typeof condition.combinationMethod === "string" ? condition.combinationMethod : "";

const conditionStateIdentity = (condition: AdvancedSelectionCondition): SharingIdentityCondition | null => {
  if (condition.isComplement === null || condition.__typename === "Empty") {
    return null;
  }
  if (condition.__typename === "Teams") {
    return condition.teams.length === 0
      ? null
      : { __typename: "TeamTag", isComplement: condition.isComplement, teams: condition.teams.map(team => ({ id: team.id })) };
  }
  if (condition.__typename === "Positions") {
    return condition.positions.length === 0
      ? null
      : {
          __typename: "PositionTag",
          isComplement: condition.isComplement,
          positions: condition.positions.map(position => ({ id: position.id })),
        };
  }
  return condition.tags.length === 0
    ? null
    : {
        __typename: "ComboTag",
        isComplement: condition.isComplement,
        combinationMethod: combinationMethodOf(condition),
        namedChildTags: condition.tags.map(tag => ({ tag: { id: tag.tag.id } })),
      };
};

const advancedSelectionStateIdentity = (state: AdvancedSelectionState): SharingIdentitySelection => ({
  includeAthletes: state.includeAthletes.map(athlete => ({ id: athlete.id })),
  excludeAthletes: state.excludeAthletes.map(athlete => ({ id: athlete.id })),
  groups: state.groups.flatMap(group => {
    const conditions = group.conditions.flatMap(condition => {
      const identity = conditionStateIdentity(condition);
      return identity ? [identity] : [];
    });
    return conditions.length === 0 ? [] : [{ conditions }];
  }),
});

export const sharingFormKey = (teams: readonly { id: string }[], advancedSelection: AdvancedSelectionState | null): string => {
  if (advancedSelection) {
    return `advanced:${advancedSelectionIdentityKey(advancedSelectionStateIdentity(advancedSelection))}`;
  }
  if (teams.length === 0) {
    return "empty";
  }
  return `teams:${[...teams.map(team => team.id)].sort().join(",")}`;
};

export const menuSharingIdentityKey = (
  hasAdvancedSelection: boolean,
  teams: readonly { id: string }[] | null,
  advancedSelection: SharingIdentitySelection | null
): string => {
  if (!hasAdvancedSelection) {
    return sharingFormKey(teams ?? [], null);
  }
  return advancedSelection ? `advanced:${advancedSelectionIdentityKey(advancedSelection)}` : "advanced:pending";
};

export const advancedSelectionIdentityKey = (selection: SharingIdentitySelection): string => {
  const groups = selection.groups.map(group => group.conditions.map(conditionIdentityKey).sort().join("&")).sort();
  return JSON.stringify({
    includeAthletes: sortedIds(selection.includeAthletes.map(athlete => athlete.id)),
    excludeAthletes: sortedIds(selection.excludeAthletes.map(athlete => athlete.id)),
    groups,
  });
};

export const sharedAdvancedSelection = <T extends SharingIdentitySelection>(selections: readonly (T | null | undefined)[]): T | null => {
  const first = selections[0];
  if (!first) {
    return null;
  }
  const key = advancedSelectionIdentityKey(first);
  const matches = selections.every(selection => selection != null && advancedSelectionIdentityKey(selection) === key);
  return matches ? first : null;
};

export const sharingSelectionsDiffer = (menus: readonly ComparableMenuSharing[]): boolean => {
  if (menus.length < 2 || menus.some(menu => menu.status !== "ready")) {
    return false;
  }
  return new Set(menus.map(menu => menu.identityKey)).size > 1;
};

export const formatMenuScheduleLabel = (start: Date, durationInMinutes: number, timezone: string): string => {
  const dateLabel = formatDateInTimezone(start, timezone, enUS, { format: "EEE, MMM d" });
  const timeLabel = formatTimeRangeInTimezone(start, durationInMinutes, timezone, {
    excludeTimezoneSuffix: true,
  });
  return `${dateLabel} · ${timeLabel}`;
};
